// Lightweight keymap layer. Each binding is { combo, handler } where combo
// is something like "Mod+K" (Mod = Cmd on macOS, Ctrl elsewhere). Bindings
// are scoped: app-wide bindings live on document; in-component bindings can
// be attached via the returned `bindWithin` ref callback.

import { useEffect } from "react";

type Combo = string;
type Handler = (e: KeyboardEvent) => void;

function isMac(): boolean {
  return typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
}

function matches(combo: Combo, e: KeyboardEvent): boolean {
  const parts = combo.toLowerCase().split("+");
  const key = parts[parts.length - 1];
  const want = new Set(parts.slice(0, -1));
  const has = (m: string) => want.has(m);
  const mod = isMac() ? e.metaKey : e.ctrlKey;
  if (has("mod") && !mod) return false;
  if (has("shift") && !e.shiftKey) return false;
  if (has("alt") && !e.altKey) return false;
  if (!has("mod") && (e.metaKey || e.ctrlKey)) {
    // Allow Cmd/Ctrl + key only if Mod is listed.
    return false;
  }
  return e.key.toLowerCase() === key;
}

export function useHotkey(combo: Combo, handler: Handler, opts: { active?: boolean } = {}): void {
  const active = opts.active !== false;
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      // Don't fire when typing into a text input unless the binding is Mod+…
      const t = e.target as HTMLElement | null;
      const inField = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || (t as HTMLElement).isContentEditable);
      const isModBinding = combo.toLowerCase().includes("mod+");
      if (inField && !isModBinding) return;
      if (matches(combo, e)) {
        e.preventDefault();
        handler(e);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [combo, handler, active]);
}

export function formatCombo(combo: Combo): string {
  const mod = isMac() ? "⌘" : "Ctrl";
  return combo
    .replace(/\bMod\b/i, mod)
    .replace(/\bShift\b/i, "⇧")
    .replace(/\bAlt\b/i, isMac() ? "⌥" : "Alt")
    .replace(/\+/g, isMac() ? "" : "+");
}
